import { useState, useEffect } from 'react';
import SessionManager from '../utils/sessionManager';

/**
 * Custom hook for authentication state management
 * Provides current session info and auth utilities
 */
export const useAuth = () => {
  const [currentSession, setCurrentSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get current session on mount
    const session = SessionManager.getCurrentSession();
    console.log('🔍 useAuth: Initial session check:', session);
    
    // Log token info for debugging
    if (session?.access_token) {
      const token = session.access_token;
      console.log('🔍 useAuth: Token preview:', token.substring(0, 50) + '...');
      console.log('🔍 useAuth: Token length:', token.length);
      console.log('🔍 useAuth: Token parts:', token.split('.').length);
    }
    
    // Check for legacy sessions and migrate if needed
    const migrated = SessionManager.migrateLegacySession();
    if (migrated) {
      console.log('🔄 useAuth: Migrated legacy session');
      const newSession = SessionManager.getCurrentSession();
      console.log('🔍 useAuth: Session after migration:', newSession);
      setCurrentSession(newSession);
    } else {
      setCurrentSession(session);
    }
    
    setIsLoading(false);

    // Listen for storage changes (when user logs in/out in another tab)
    const handleStorageChange = (e) => {
      if (e.key && e.key.startsWith('dutlaunch_')) {
        console.log('🔄 useAuth: Storage changed:', e.key);
        const updatedSession = SessionManager.getCurrentSession();
        console.log('🔍 useAuth: Updated session:', updatedSession);
        setCurrentSession(updatedSession);
      }
    };

    // Listen for custom session update events
    const handleSessionUpdate = (e) => {
      console.log('🔄 useAuth: Session updated event:', e.detail);
      const updatedSession = SessionManager.getCurrentSession();
      console.log('🔍 useAuth: Updated session from event:', updatedSession);
      setCurrentSession(updatedSession);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('sessionUpdated', handleSessionUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sessionUpdated', handleSessionUpdate);
    };
  }, []);

  const login = (role, sessionData) => {
    SessionManager.setSession(role, sessionData);
    setCurrentSession(SessionManager.getCurrentSession());
  };

  const logout = (role = null) => {
    console.log('🚪 Logout called with role:', role);
    const currentSessionBefore = SessionManager.getCurrentSession();
    console.log('🔍 Current session before logout:', currentSessionBefore);
    
    if (role) {
      SessionManager.clearSession(role);
    } else {
      // Logout current session
      const current = SessionManager.getCurrentSession();
      if (current) {
        console.log('🗑️ Clearing session for role:', current.role);
        SessionManager.clearSession(current.role);
      }
    }
    // Force clear current session to ensure no automatic switching
    localStorage.removeItem('dutlaunch_current_session');
    console.log('🧹 Cleared current session key');
    
    const currentSessionAfter = SessionManager.getCurrentSession();
    console.log('🔍 Current session after logout:', currentSessionAfter);
    
    setCurrentSession(null);
    console.log('✅ Logout complete');
  };

  const logoutAll = () => {
    console.log('🚪 Logout ALL sessions called');
    SessionManager.clearAllSessions();
    setCurrentSession(null);
    console.log('✅ All sessions cleared');
  };

  const switchSession = (role) => {
    const session = SessionManager.switchSession(role);
    setCurrentSession(session);
    return session;
  };

  const isLoggedIn = (role = null) => {
    if (role) {
      return SessionManager.isLoggedIn(role);
    }
    return currentSession && currentSession.access_token;
  };

  const getToken = (role = null) => {
    if (role) {
      const session = SessionManager.getSession(role);
      return session?.access_token;
    }
    return currentSession?.access_token;
  };

  const getUserId = (role = null) => {
    if (role) {
      const session = SessionManager.getSession(role);
      return session?.user_id;
    }
    return currentSession?.user_id;
  };

  const getProfile = (role = null) => {
    if (role) {
      const session = SessionManager.getSession(role);
      return session?.profile;
    }
    return currentSession?.profile;
  };

  const getAllSessions = () => {
    return SessionManager.getAllSessions();
  };

  const refreshCurrentSession = () => {
    const session = SessionManager.getCurrentSession();
    console.log('🔄 useAuth: Refreshing current session:', session);
    setCurrentSession(session);
    return session;
  };

  const updateSessionProfile = (role, profileData) => {
    const session = SessionManager.getSession(role);
    if (session) {
      const updatedSessionData = {
        ...session,
        profile: {
          ...session.profile,
          ...profileData
        }
      };
      SessionManager.setSession(role, updatedSessionData);
      
      // If this is the current session, update the state
      const currentRole = SessionManager.getCurrentSession()?.role;
      if (currentRole === role) {
        setCurrentSession(updatedSessionData);
      }
      
      console.log('🔄 useAuth: Updated session profile for role:', role, profileData);
      return updatedSessionData;
    }
    return null;
  };

  return {
    currentSession,
    isLoading,
    isLoggedIn,
    login,
    logout,
    logoutAll,
    switchSession,
    refreshCurrentSession,
    updateSessionProfile,
    getToken,
    getUserId,
    getProfile,
    getAllSessions,
    // Convenience getters for current session
    token: currentSession?.access_token,
    role: currentSession?.role,
    userId: currentSession?.user_id,
    email: currentSession?.email,
    profile: currentSession?.profile,
  };
};

/**
 * Legacy compatibility function
 * Returns session data in the old format for backward compatibility
 */
export const getLegacyAuthData = () => {
  const session = SessionManager.getCurrentSession();
  if (!session) {
    return {
      token: null,
      role: null,
      userId: null,
      email: null,
    };
  }

  return {
    token: session.access_token,
    role: session.role,
    userId: session.user_id,
    email: session.email,
    username: session.username,
    company: session.company,
    contactName: session.contact_name,
  };
};

export default useAuth;
